/**
 * QuestionsAnalysis Component
 *
 * Analytics component for question statistics, distribution by module/exam type,
 * and usage metrics. Displays question counts, level distribution, module breakdown,
 * and most/least used questions.
 *
 * @module QuestionsAnalysis
 */

import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  FileQuestion,
  BarChart3,
  TrendingUp,
  Award,
  BookOpen,
  Activity,
  Loader2,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import QuestionPerformance from './QuestionPerformance';
import { getQuestionsAnalytics } from '@/lib/analyticsApi';
import { CEFR_LEVELS, MODULES } from '@/lib/constants';
import { logger } from '@/lib/logger';

interface QuestionMetrics {
  totalQuestions: number;
  questionsByLevel: number;
  questionsByModule: number;
  questionsByExamType: number;
  questionsThisWeek: number;
}

interface LevelDistribution {
  level: string;
  count: number;
  percentage: number;
}

interface ModuleDistribution {
  module: string;
  count: number;
  percentage: number;
}

interface ExamTypeDistribution {
  examType: string;
  count: number;
  percentage: number;
}

interface QuestionUsage {
  questionId: string;
  questionText: string;
  usageCount: number;
  level: string;
  module: string;
  section: number | null;
  examType: string[];
}

// Helper function to get module display name
const getModuleDisplayName = (module: string): string => {
  const moduleMap: Record<string, string> = {
    reading: 'Reading',
    writing: 'Writing',
    listening: 'Listening',
    speaking: 'Speaking',
  };
  return moduleMap[module.toLowerCase()] || module;
};

export default function QuestionsAnalysis() {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<QuestionMetrics>({
    totalQuestions: 0,
    questionsByLevel: 0,
    questionsByModule: 0,
    questionsByExamType: 0,
    questionsThisWeek: 0,
  });
  const [levelDistribution, setLevelDistribution] = useState<
    LevelDistribution[]
  >([]);
  const [moduleDistribution, setModuleDistribution] = useState<
    ModuleDistribution[]
  >([]);
  const [examTypeDistribution, setExamTypeDistribution] = useState<
    ExamTypeDistribution[]
  >([]);
  const [mostUsedQuestions, setMostUsedQuestions] = useState<QuestionUsage[]>(
    [],
  );

  const [leastUsedQuestions, setLeastUsedQuestions] = useState<QuestionUsage[]>(
    [],
  );
  const [selectedLevelExamType, setSelectedLevelExamType] =
    useState<string>('all');
  const [selectedModuleExamType, setSelectedModuleExamType] =
    useState<string>('all');

  // Usage filters
  const [usageExamType, setUsageExamType] = useState<string>('all');
  const [usageLevel, setUsageLevel] = useState<string>('all');
  const [usageModule, setUsageModule] = useState<string>('all');
  const [usageSection, setUsageSection] = useState<string>('all');

  useEffect(() => {
    const fetchQuestionsAnalytics = async () => {
      try {
        setLoading(true);
        const filters: Record<string, string> = {};

        if (selectedLevelExamType !== 'all') {
          filters.levelExamType = selectedLevelExamType;
        }
        if (selectedModuleExamType !== 'all') {
          filters.moduleExamType = selectedModuleExamType;
        }
        if (usageExamType !== 'all') {
          filters.usageExamType = usageExamType;
        }
        if (usageLevel !== 'all') {
          filters.usageLevel = usageLevel;
        }
        if (usageModule !== 'all') {
          filters.usageModule = usageModule;
        }
        if (usageSection !== 'all') {
          filters.usageSection = usageSection;
        }

        const data = await getQuestionsAnalytics(filters);

        // Set metrics
        setMetrics(
          data.metrics || {
            totalQuestions: 0,
            questionsByLevel: 0,
            questionsByModule: 0,
            questionsByExamType: 0,
            questionsThisWeek: 0,
          },
        );

        // Set distributions
        setLevelDistribution(data.levelDistribution || []);

        // Capitalize module names for display
        const formattedModuleDistribution = (data.moduleDistribution || []).map(
          (item: ModuleDistribution) => ({
            ...item,
            module: item.module.charAt(0).toUpperCase() + item.module.slice(1),
          }),
        );
        setModuleDistribution(formattedModuleDistribution);

        // Capitalize exam type names for display
        const formattedExamTypeDistribution = (
          data.examTypeDistribution || []
        ).map((item: ExamTypeDistribution) => ({
          ...item,
          examType:
            item.examType.charAt(0).toUpperCase() + item.examType.slice(1),
        }));
        setExamTypeDistribution(formattedExamTypeDistribution);

        // Format usage questions with capitalized module names
        const formatUsageQuestions = (questions: QuestionUsage[]) => {
          return (questions || []).map((q: QuestionUsage) => ({
            ...q,
            module: q.module.charAt(0).toUpperCase() + q.module.slice(1),
            examType: Array.isArray(q.examType) ? q.examType : [],
          }));
        };
        setMostUsedQuestions(
          formatUsageQuestions(data.mostUsedQuestions || []),
        );
        setLeastUsedQuestions(
          formatUsageQuestions(data.leastUsedQuestions || []),
        );
      } catch (error) {
        logger.error('Error fetching questions analytics:', error);
        // Set empty data on error
        setMetrics({
          totalQuestions: 0,
          questionsByLevel: 0,
          questionsByModule: 0,
          questionsByExamType: 0,
          questionsThisWeek: 0,
        });
        setLevelDistribution([]);
        setModuleDistribution([]);
        setExamTypeDistribution([]);
        setMostUsedQuestions([]);
        setLeastUsedQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionsAnalytics();
  }, [
    selectedLevelExamType,
    selectedModuleExamType,
    usageExamType,
    usageLevel,
    usageModule,
    usageSection,
  ]);

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-ui">
                  Total Questions
                </p>
                <p className="text-3xl font-bold text-primary font-ui">
                  {loading ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    metrics.totalQuestions.toLocaleString()
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1 font-ui">
                  All exam questions
                </p>
              </div>
              <FileQuestion className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-ui">
                  Questions by Level
                </p>
                <p className="text-3xl font-bold text-primary font-ui">
                  {loading ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    levelDistribution.length
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1 font-ui">
                  CEFR levels covered
                </p>
              </div>
              <Award className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-ui">
                  Questions by Module
                </p>
                <p className="text-3xl font-bold text-primary font-ui">
                  {loading ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    moduleDistribution.length
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1 font-ui">
                  Modules available
                </p>
              </div>
              <BookOpen className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-ui">
                  New Questions
                </p>
                <p className="text-3xl font-bold text-primary font-ui">
                  {loading ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    metrics.questionsThisWeek
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1 font-ui">
                  Added this week
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Level Distribution */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 font-ui">
                <BarChart3 className="h-5 w-5" />
                Question Level Distribution
              </CardTitle>
              <CardDescription className="font-ui">
                Distribution of questions across CEFR levels
              </CardDescription>
            </div>
            <Select
              value={selectedLevelExamType}
              onValueChange={setSelectedLevelExamType}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Exam Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-ui">All</span>
                </SelectItem>
                <SelectItem value="goethe">
                  <span className="font-ui">Goethe</span>
                </SelectItem>
                <SelectItem value="osd">
                  <span className="font-ui">OSD</span>
                </SelectItem>
                <SelectItem value="telc">
                  <span className="font-ui">Telc</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground font-ui">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : levelDistribution.length > 0 ? (
              levelDistribution.map((item) => (
                <div key={item.level} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-ui">
                        {item.level}
                      </Badge>
                      <span className="text-sm font-medium font-ui">
                        {item.count} questions
                      </span>
                    </div>
                    <span className="text-sm text-muted-foreground font-ui">
                      {item.percentage}%
                    </span>
                  </div>
                  <Progress value={item.percentage} className="h-2" />
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground font-ui">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Module Distribution */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 font-ui">
                <BookOpen className="h-5 w-5" />
                Module Distribution
              </CardTitle>
              <CardDescription className="font-ui">
                Distribution of questions across modules
              </CardDescription>
            </div>
            <Select
              value={selectedModuleExamType}
              onValueChange={setSelectedModuleExamType}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Exam Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-ui">All</span>
                </SelectItem>
                <SelectItem value="goethe">
                  <span className="font-ui">Goethe</span>
                </SelectItem>
                <SelectItem value="osd">
                  <span className="font-ui">OSD</span>
                </SelectItem>
                <SelectItem value="telc">
                  <span className="font-ui">Telc</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground font-ui">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : moduleDistribution.length > 0 ? (
              moduleDistribution.map((item) => (
                <div key={item.module} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-ui">
                        {getModuleDisplayName(item.module)}
                      </Badge>
                      <span className="text-sm font-medium font-ui">
                        {item.count} questions
                      </span>
                    </div>
                    <span className="text-sm text-muted-foreground font-ui">
                      {item.percentage}%
                    </span>
                  </div>
                  <Progress value={item.percentage} className="h-2" />
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground font-ui">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Exam Type Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-ui">
            <Activity className="h-5 w-5" />
            Exam Type Distribution
          </CardTitle>
          <CardDescription className="font-ui">
            Distribution of questions across exam types
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground col-span-3 font-ui">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : examTypeDistribution.length > 0 ? (
              examTypeDistribution.map((item) => (
                <div key={item.examType} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Badge variant="outline" className="font-ui">
                      {item.examType}
                    </Badge>
                    <span className="text-sm text-muted-foreground font-ui">
                      {item.percentage}%
                    </span>
                  </div>
                  <Progress value={item.percentage} className="h-2" />
                  <p className="text-sm font-medium text-center font-ui">
                    {item.count} questions
                  </p>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground col-span-3 font-ui">
                No data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Usage Analysis Filters */}
      <div className="flex flex-wrap gap-4 items-center justify-end bg-muted/30 p-4 rounded-lg">
        <span className="text-sm font-medium text-muted-foreground mr-auto flex items-center gap-2 font-ui">
          <TrendingUp className="h-4 w-4" />
          Usage Analysis Filters
        </span>

        <Select value={usageExamType} onValueChange={setUsageExamType}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Exam Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <span className="font-ui">All Types</span>
            </SelectItem>
            <SelectItem value="goethe">
              <span className="font-ui">Goethe</span>
            </SelectItem>
            <SelectItem value="osd">
              <span className="font-ui">OSD</span>
            </SelectItem>
            <SelectItem value="telc">
              <span className="font-ui">Telc</span>
            </SelectItem>
          </SelectContent>
        </Select>

        <Select value={usageLevel} onValueChange={setUsageLevel}>
          <SelectTrigger className="w-[100px]">
            <SelectValue placeholder="Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <span className="font-ui">All Levels</span>
            </SelectItem>
            {CEFR_LEVELS.map((level) => (
              <SelectItem key={level} value={level}>
                <span className="font-ui">{level}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={usageModule} onValueChange={setUsageModule}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Module" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <span className="font-ui">All Modules</span>
            </SelectItem>
            {MODULES.map((mod) => (
              <SelectItem key={mod.value} value={mod.value}>
                <span className="font-ui">{getModuleDisplayName(mod.value)}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={usageSection} onValueChange={setUsageSection}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Section" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <span className="font-ui">All Sections</span>
            </SelectItem>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
              <SelectItem key={num} value={String(num)}>
                <span className="font-ui">Section {num}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Most Used Questions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-ui">
              <TrendingUp className="h-5 w-5" />
              Most Used Questions
            </CardTitle>
            <CardDescription className="font-ui">
              Questions with the highest usage count
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground font-ui">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                  Loading...
                </div>
              ) : mostUsedQuestions.length > 0 ? (
                mostUsedQuestions.map((question, index) => (
                  <div
                    key={question.questionId}
                    className="flex items-start justify-between p-3 bg-muted/50 rounded-lg gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-bold text-primary font-ui">
                          #{index + 1}
                        </span>
                        <Badge variant="outline" className="text-xs font-ui">
                          {question.level}
                        </Badge>
                        <Badge variant="secondary" className="text-xs font-ui">
                          {getModuleDisplayName(question.module)}
                        </Badge>
                        {question.section && (
                          <Badge variant="outline" className="text-xs font-ui">
                            Section {question.section}
                          </Badge>
                        )}
                        {question.examType && question.examType.length > 0 && (
                          <Badge variant="outline" className="text-xs font-ui">
                            {question.examType
                              .map(
                                (et: string) =>
                                  et.charAt(0).toUpperCase() + et.slice(1),
                              )
                              .join(', ')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-medium break-words font-ui">
                        {question.questionText}
                      </p>
                    </div>
                    <div className="ml-4 text-right flex-shrink-0">
                      <p className="text-sm font-bold text-primary font-ui">
                        {question.usageCount}
                      </p>
                      <p className="text-xs text-muted-foreground font-ui">
                        uses
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground font-ui">
                  No data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Least Used Questions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-ui">
              <Activity className="h-5 w-5" />
              Least Used Questions
            </CardTitle>
            <CardDescription className="font-ui">
              Questions with the lowest usage count
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground font-ui">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                  Loading...
                </div>
              ) : leastUsedQuestions.length > 0 ? (
                leastUsedQuestions.map((question, index) => (
                  <div
                    key={question.questionId}
                    className="flex items-start justify-between p-3 bg-muted/50 rounded-lg gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-bold text-muted-foreground font-ui">
                          #{index + 1}
                        </span>
                        <Badge variant="outline" className="text-xs font-ui">
                          {question.level}
                        </Badge>
                        <Badge variant="secondary" className="text-xs font-ui">
                          {getModuleDisplayName(question.module)}
                        </Badge>
                        {question.section && (
                          <Badge variant="outline" className="text-xs font-ui">
                            Section {question.section}
                          </Badge>
                        )}
                        {question.examType && question.examType.length > 0 && (
                          <Badge variant="outline" className="text-xs font-ui">
                            {question.examType
                              .map(
                                (et: string) =>
                                  et.charAt(0).toUpperCase() + et.slice(1),
                              )
                              .join(', ')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-medium break-words font-ui">
                        {question.questionText}
                      </p>
                    </div>
                    <div className="ml-4 text-right flex-shrink-0">
                      <p className="text-sm font-bold text-muted-foreground font-ui">
                        {question.usageCount}
                      </p>
                      <p className="text-xs text-muted-foreground font-ui">
                        uses
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground font-ui">
                  No data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Performance Analysis */}
      <QuestionPerformance />
    </div>
  );
}
